import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [billing, setBilling] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(true);

  const refreshBilling = useCallback(async () => {
    try { const r = await api.get("/billing/me"); setBilling(r.data); return r.data; }
    catch { setBilling({ active: false, limits: {}, usage: {} }); return null; }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("pp_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await refreshBilling(); })
      .catch(() => { localStorage.removeItem("pp_token"); })
      .finally(() => setLoading(false));
  }, [refreshBilling]);

  const login = async (email, password, role) => {
    const { data } = await api.post("/auth/login", { email, password, role });
    localStorage.setItem("pp_token", data.token);
    setUser(data.user);
    await refreshBilling();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("pp_token", data.token);
    setUser(data.user);
    await refreshBilling();
    return data.user;
  };

  const logout = () => {
    window.__ppLoggingOut = true;
    // pass the token explicitly: axios request interceptors run async, i.e. after removeItem below
    const token = localStorage.getItem("pp_token");
    if (token) api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem("pp_token");
    setUser(null);
    setBilling(null);
    setTimeout(() => { window.__ppLoggingOut = false; }, 1000);
  };

  const limits = billing?.limits || {};
  const can = (feature) => billing?.active && limits[feature] !== false && limits[feature] !== 0;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, billing, refreshBilling, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
